"use client";

import { getCoreRowModel, getFilteredRowModel, useReactTable } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import type { FilterClause } from "../domain";
import { PROCESSING_MODE_OBJ } from "../domain";
import { applyFilterClauseToRow } from "../app";
import { getColumnName } from "./column-helpers";
import { useColumnLayout } from "./hooks/use-column-layout";
import { useFilterHistory } from "./hooks/use-filter-history";
import { useRowEditing } from "./hooks/use-row-editing";
import { useUrlStateSync } from "./hooks/use-url-state-sync";
import { nextSortClauses } from "../app";
import { useTableLabelFn } from "./labels-context";
import { useTableComponents, useTableConfig } from "./table-provider";


import type { SmartTableProps } from "./smart-table-props";
import { EMPTY_COLUMN_GROUPS, EMPTY_COLUMN_META, EMPTY_FILTERS, EMPTY_LISTS, EMPTY_STRING_ARRAY, MAX_CLIENT_ROWS } from "./smart-table-shared";

export function useSmartTableModel<TData>(props: SmartTableProps<TData>) {
  const {

  columns,
  data,
  total,
  page,
  pageSize,
  isLoading,
  error,
  filters,
  sort,
  clientFilters = EMPTY_FILTERS,
  presets,
  activePresetId,
  visibleColumnIds,
  stickyColumns = EMPTY_STRING_ARRAY,
  hiddenColumns = EMPTY_STRING_ARRAY,
  columnGroups = EMPTY_COLUMN_GROUPS,
  columnMeta = EMPTY_COLUMN_META,
  lists = EMPTY_LISTS,
  enableColumnResizing = true,
  enableColumnReorder = true,
  enableUrlState = false,
  onPageChange,
  onPageSizeChange,
  onFiltersChange,
  onSortChange,
  onPresetChange,
  onVisibleColumnsChange,
  onRowClick,
  onExport,
  exportLabel,
  toolbarActions,
  enableBulkEdit = false,
  onBulkSave,
  bulkActions,
  onSelectedRowsChange,
  onInlineEdit,
  isRowEditable: isRowEditableRow,
  rowIdField = "id",
  virtualizeRows = false,
  virtualRowHeight = 40,
  virtualOverscan = 8,
  } = props;
  const t = useTableLabelFn();
  const { Button, Select, EmptyState, Loading, ErrorState } = useTableComponents();
  const { locale } = useTableConfig();
  const [showFilters, setShowFilters] = useState(false);
  // filter popover state — opened from header Funnel icon or 'f' key
  const [filterPopoverColumn, setFilterPopoverColumn] = useState<string | null>(
    null,
  );
  // hovered filter chip → highlight matching rows
  const [hoveredFilter, setHoveredFilter] = useState<FilterClause | null>(null);
  const [quickFilter, setQuickFilter] = useState<{
    column: string;
    value: unknown;
    x: number;
    y: number;
  } | null>(null);
  const defaultFilter = useMemo(() => {
    const activePreset =
      activePresetId && presets
        ? presets.find((p) => p.id === activePresetId)
        : undefined;
    return activePreset?.defaultFilter;
  }, [activePresetId, presets]);

  const { recent, onFiltersChangeWithHistory } = useFilterHistory({ filters, onFiltersChange });
  const [showColumns, setShowColumns] = useState(false);
  const [columnSearch, setColumnSearch] = useState("");
  const headerMouse = useRef<{ x: number; y: number; down: boolean } | null>(
    null,
  );
  const rowMouse = useRef<{
    rowId: string;
    startX: number;
    startY: number;
    moved: boolean;
  } | null>(null);

  const actionColumnIds = useMemo(
    () =>
      columns
        .filter((c) => c.meta?.isAction)
        .map((c) => getColumnName(c))
        .filter((id): id is string => typeof id === "string"),
    [columns],
  );

  const { bulkEditMode, setBulkEditMode, selectedRowIds, setSelectedRowIds, bulkEdits, isBulkSaving, bulkSaveError, inlineEditCell, setInlineEditCell, commonRowValues, setCommonRowValues, getRowId, editableColumns, commonRowSummary, toggleRowSelection, toggleAllVisible, setCellEdit, handleBulkSave, handleBulkCancel, handleInlineEditSave, handleInlineEditCancel } = useRowEditing<TData>({
    columns,
    data,
    filters,
    page,
    pageSize,
    rowIdField,
    isRowEditable: isRowEditableRow,
    onBulkSave,
    onInlineEdit,
    onSelectedRowsChange,
  });

  const { columnSizing, setColumnSizing, columnOrder, setColumnOrder, setDraggedColumn, localColumnVisibility, setLocalColumnVisibility, stickyIds, scrollContainerRef, handleTableScroll, applyStickyTransform, hiddenIds, handleColumnDrop } = useColumnLayout<TData>({
    columns,
    visibleColumnIds,
    stickyColumns,
    hiddenColumns,
    actionColumnIds,
  });

  const displayData = useMemo(() => {
    if (data.length > MAX_CLIENT_ROWS) return data.slice(0, MAX_CLIENT_ROWS);
    return data;
  }, [data]);

  const isRowLimited = data.length > MAX_CLIENT_ROWS;

  const table = useReactTable({
    columns,
    data: displayData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableColumnResizing,
    columnResizeMode: "onChange",
    pageCount: Math.ceil(total / pageSize),
    state: {
      columnVisibility: localColumnVisibility,
      columnSizing,
      columnOrder,
    },
    onColumnVisibilityChange: (updater) => {
      setLocalColumnVisibility((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        if (onVisibleColumnsChange) {
          const allIds = columns
            .map((c) => getColumnName(c))
            .filter((id): id is string => typeof id === "string");
          const nonActionIds = allIds.filter(
            (id) => !actionColumnIds.includes(id),
          );
          const visibleIds = nonActionIds.filter((id) => next[id] !== false);
          onVisibleColumnsChange(
            visibleIds.length === nonActionIds.length ? null : visibleIds,
          );
        }
        return next;
      });
    },
    onColumnSizingChange: setColumnSizing,
    onColumnOrderChange: setColumnOrder,
    globalFilterFn: (row) => {
      return clientFilters.every((clause) =>
        applyFilterClauseToRow(row.original, clause),
      );
    },
  });

  const visibleCols = table.getVisibleLeafColumns();
  const totalColumnWidth = visibleCols.reduce(
    (sum, col) => sum + col.getSize(),
    0,
  );
  useEffect(() => {
    table.setGlobalFilter(clientFilters.length > 0 ? "on" : undefined);
  }, [clientFilters]);

  useUrlStateSync({
    page,
    pageSize,
    filters,
    sort,
    activePresetId,
    stickyIds,
    hiddenIds,
    enableUrlState,
  });

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  const toggleSort = (columnId: string, shiftKey: boolean) => {
    if (actionColumnIds.includes(columnId)) return;
    const meta = columns.find((c) => getColumnName(c) === columnId)?.meta;
    if (meta?.sortable === PROCESSING_MODE_OBJ.NONE) return;

    onSortChange(nextSortClauses(sort, columnId, shiftKey));
  };

  const removeFilter = (idx: number) => {
    onFiltersChangeWithHistory(filters.filter((_, i) => i !== idx));
  };

  return { columns, data, total, page, pageSize, isLoading, error, filters, sort, clientFilters, presets, activePresetId, visibleColumnIds, stickyColumns, hiddenColumns, columnGroups, columnMeta, lists, enableColumnResizing, enableColumnReorder, enableUrlState, onPageChange, onPageSizeChange, onFiltersChange, onSortChange, onPresetChange, onVisibleColumnsChange, onRowClick, onExport, exportLabel, toolbarActions, enableBulkEdit, onBulkSave, bulkActions, onSelectedRowsChange, onInlineEdit, isRowEditableRow, rowIdField, showFilters, setShowFilters, filterPopoverColumn, setFilterPopoverColumn, hoveredFilter, setHoveredFilter, quickFilter, setQuickFilter, showColumns, setShowColumns, columnSearch, setColumnSearch, t, defaultFilter, headerMouse, rowMouse, actionColumnIds, displayData, isRowLimited, table, visibleCols, totalColumnWidth, totalPages, startItem, endItem, toggleSort, removeFilter, Button, Select, EmptyState, Loading, ErrorState, locale, recent, onFiltersChangeWithHistory, bulkEditMode, setBulkEditMode, selectedRowIds, setSelectedRowIds, bulkEdits, isBulkSaving, bulkSaveError, inlineEditCell, setInlineEditCell, commonRowValues, setCommonRowValues, getRowId, editableColumns, commonRowSummary, toggleRowSelection, toggleAllVisible, setCellEdit, handleBulkSave, handleBulkCancel, handleInlineEditSave, handleInlineEditCancel, columnSizing, setColumnSizing, columnOrder, setColumnOrder, setDraggedColumn, localColumnVisibility, setLocalColumnVisibility, stickyIds, scrollContainerRef, handleTableScroll, hiddenIds, handleColumnDrop, virtualizeRows, virtualRowHeight, virtualOverscan, applyStickyTransform };
}

export type SmartTableModel<TData> = ReturnType<typeof useSmartTableModel<TData>>;
